/**
 * drawerNoProvider — giá trị context khi *DrawerProvider KHÔNG nằm trên cây React của
 * người gọi. Hai ca rất khác nhau cùng rơi vào đây, và ca thứ hai mới là ca hay gặp:
 *
 * a) Host không mount Provider (bản triển khai không có module đó). Không ai đăng ký
 *    ⇒ click không làm gì, đúng như trước.
 *
 * b) **Provider CÓ mount nhưng người gọi không phải con cháu nó.** Mọi *DrawerProvider
 *    dựng drawer của mình làm ANH EM của `{children}`:
 *
 *        <Ctx.Provider value={...}>
 *          {children}            ← các Provider TRONG nằm ở đây
 *          {id && <XxxDrawer/>}  ← anh em, nên NGOÀI mọi Provider TRONG
 *        </Ctx.Provider>
 *
 *    `main.jsx` lồng 13 Provider, nên nội dung xem TRONG một drawer chỉ thấy context của
 *    các Provider **bọc ngoài** drawer đó. Ví dụ đã thành sự cố: CrmInvoiceDrawerProvider
 *    bọc ngoài FulfillmentOrderDrawerProvider ⇒ hoá đơn xem trong drawer bấm thẻ «Đơn
 *    triển khai» CHẾT CÂM — không lỗi, không log (sếp Phú 18.09.2026). Cùng component đó
 *    mở thẳng trang `/crm/invoices/:id` thì lại chạy, nên rất dễ tưởng "máy tôi ổn".
 *
 *    Sổ đăng ký của `utils/openEntity` nằm ở **module scope** (không phải React context)
 *    và chính Provider đã ghi tên vào đó lúc mount ⇒ vẫn với tới được `openXxx` THẬT. Nó
 *    chỉ set query param, rồi Provider tự dựng drawer. Drawer portal vào `document.body`
 *    và mount SAU nên tự nằm trên — không phải chỉnh z-index.
 *
 * ⚠️ Đây là đường LUI, không phải cách làm chuẩn. Cách gốc là tách "Provider" khỏi "chỗ
 *    dựng drawer" rồi dựng hết drawer ở lớp trong cùng; chưa làm vì đổi hình dạng Provider
 *    là phải sửa `main.jsx` của cả Core lẫn SaaS cùng lúc (quên một chỗ = mất hẳn drawer).
 *
 * `closeXxx` / `openXxxId` KHÔNG đi đường vòng này được (sổ đăng ký chỉ có hàm mở) — chỉ
 * drawer của chính loại đó mới cần tới chúng, mà nó thì luôn nằm trong Provider của mình.
 */
import { getEntityOpener } from './openEntity';

/**
 * Hàm mở popup cho giá trị context dự phòng. Tra sổ đăng ký lúc BẤM, không phải lúc vẽ —
 * Provider đăng ký trong `useEffect` nên lượt vẽ đầu tiên sổ còn rỗng.
 *
 * @param {string} kind tên loại trong ENTITY_LINK_RULES (utils/openEntity)
 */
export function noProviderOpener(kind) {
  return (id) => {
    if (!id) return;
    const open = getEntityOpener(kind);
    if (!open) {
      // Không Provider nào mount trong cả app ⇒ click rơi vào hư không. Không throw (đang
      // ở trong handler, throw chỉ làm trắng trang) nhưng phải KÊU, kẻo lại chết câm.
      console.error(`[drawer] không Provider nào mount cho «${kind}» — click mở popup không làm gì.`);
      return;
    }
    open(id);
  };
}
